'use client';

import { EpaysageResponse } from '@repo/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '@repo/ui/lib/api/services';

export interface EpaysageImageUploadParams {
  epaysageId: number;
  field: 'img1' | 'img2' | 'img3';
  file: File;
}

export interface EpaysageImageDeleteParams {
  epaysageId: number;
  field: 'img1' | 'img2' | 'img3';
}

export interface EpaysageImageRotateParams {
  epaysageId: number;
  field: 'img1' | 'img2' | 'img3';
  rotation: number;
}

export function useEpaysageImageUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ epaysageId, field, file }: EpaysageImageUploadParams): Promise<EpaysageResponse> => {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Type de fichier non supporté. Utilisez JPEG, PNG ou WebP.');
      }

      const maxSize = 25 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximale: 5MB.');
      }

      return services.epaysage.updateImage(epaysageId, field, file);
    },
    onSuccess: (data, { epaysageId }) => {
      queryClient.invalidateQueries({ queryKey: ['epaysage', epaysageId] });
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
      queryClient.invalidateQueries({ queryKey: ['inventaire', data.data?.id] });
    },
  });
}

export function useEpaysageImageDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ epaysageId, field }: EpaysageImageDeleteParams): Promise<EpaysageResponse> => {
      return services.epaysage.deleteImage(epaysageId, field);
    },
    onSuccess: (_, { epaysageId }) => {
      queryClient.invalidateQueries({ queryKey: ['epaysage', epaysageId] });
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
  });
}

export function useEpaysageImageRotate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ epaysageId, field, rotation }: EpaysageImageRotateParams): Promise<EpaysageResponse> => {
      if (![0, 90, 180, 270].includes(rotation)) {
        throw new Error('La rotation doit être 0, 90, 180 ou 270 degrés');
      }

      return services.epaysage.rotateImage(epaysageId, field, rotation);
    },
    onSuccess: (_, { epaysageId }) => {
      queryClient.invalidateQueries({ queryKey: ['epaysage', epaysageId] });
      queryClient.invalidateQueries({ queryKey: ['inventaire'] });
    },
  });
}

export function useEpaysageImageOperations() {
  const upload = useEpaysageImageUpload();
  const remove = useEpaysageImageDelete();
  const rotate = useEpaysageImageRotate();

  return {
    upload,
    delete: remove,
    rotate,
    isLoading: upload.isPending || remove.isPending || rotate.isPending,
    error: upload.error || remove.error || rotate.error,
  };
}

